/**
 * Universal tooltip copy — the metricAnnotations registry's in-app
 * editorial layer. Gateway-owned (/api/tend/settings/tooltips, not
 * proxied). Reads are for every signed-in user; writes are universal-admin
 * (SuperAdmin / Customer Success) and store sparse overrides keyed by the
 * registry's stable annotation ids.
 */
import { API_BASE_URL } from '../config/api';
import { authedFetch } from '../utils/authedFetch';

const BASE = `${API_BASE_URL}/tend/settings/tooltips`;

const jsonHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export interface TooltipOverride {
  title: string;
  subtext: string;
}

export interface TooltipCopyResponse {
  overrides: Record<string, TooltipOverride>;
  can_edit: boolean;
}

export async function fetchTooltipCopy(): Promise<TooltipCopyResponse> {
  const response = await authedFetch(BASE, { headers: jsonHeaders() });
  if (!response.ok) throw new Error(`tooltip copy ${response.status}`);
  return response.json();
}

export async function saveTooltipCopy(
  annotationKey: string,
  override: TooltipOverride,
): Promise<Record<string, TooltipOverride>> {
  const response = await authedFetch(`${BASE}/${encodeURIComponent(annotationKey)}`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(override),
  });
  if (!response.ok) throw new Error(`tooltip copy save ${response.status}`);
  return (await response.json()).overrides;
}

export async function resetTooltipCopy(
  annotationKey: string,
): Promise<Record<string, TooltipOverride>> {
  const response = await authedFetch(`${BASE}/${encodeURIComponent(annotationKey)}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  if (!response.ok) throw new Error(`tooltip copy reset ${response.status}`);
  return (await response.json()).overrides;
}
